import {
  BROWSE_ROUTE,
  EDIT_DELIVERY_GROCERIES_ROUTE,
  EDIT_DELIVERY_RECIPES_ROUTE,
  DISCOVER_FEED_ROUTE,
  GUEST_MY_COOKBOOK_ROUTE,
  MY_COOKBOOK_ROUTE,
  MY_HUNGRYROOT_ROUTE,
  ORDER_ROUTE,
  ORDERS_PLACEHOLDER_ROUTE,
} from '@hungryroot/routes';
import { type InteractionTab } from '../types/FeedInteractionFields';

/**
 * Maps a location to its Exp 242 top-level nav tab, or `null` for routes that
 * don't belong to one of the four tabs. Mirrors the treatment nav's active-tab
 * logic (`getActiveNavTab` in app-mhr), plus:
 *
 * - Desktop / classic-nav Orders overlay: `?orders=true` on whatever pathname
 *   the dropdown opened over (`ClassicMobileNav.getActive`, `DropdownPopover`).
 * - Nested surfaces the treatment nav doesn't highlight: `/edit-delivery/recipes/`
 *   and `/edit-delivery/groceries/` — desktop Recipes / Groceries and the Browse
 *   index destinations — under Search, and `/order/:orderId/` under Orders.
 *
 * Route constants are trailing-slash-terminated, but at runtime the same page
 * shows up both with and without the slash, so the pathname is normalized to a
 * trailing slash before whole-segment prefix matching (no `matchPath` here —
 * this package doesn't depend on react-router).
 */
export function getNavTabFromPathname(
  pathname: string,
  search = ''
): InteractionTab | null {
  // Desktop (and classic mobile) stamp Orders as a query overlay rather than
  // navigating to `/orders/`. Check it first so it wins over the underlying page.
  if (new URLSearchParams(search).has('orders')) {
    return 'orders';
  }

  const normalized = pathname.endsWith('/') ? pathname : `${pathname}/`;

  // Home is the exact route only — sub-paths of /myhungryroot/ are not tabs.
  if (normalized === MY_HUNGRYROOT_ROUTE) {
    return 'home';
  }
  // Exp 255 freemium: the pre-auth discover feed is the guests' home surface —
  // `GuestFloatingBottomNav` labels it "Home" — so it reports the same tab as
  // `/myhungryroot/` rather than widening `InteractionTab`. Exact-match only, so
  // `/discover/recipes/` and `/discover/cookbooks/` stay untabbed as before.
  if (normalized === DISCOVER_FEED_ROUTE) {
    return 'home';
  }
  if (normalized.startsWith(MY_COOKBOOK_ROUTE)) {
    return 'cookbook';
  }
  // Prefix match so `/guest/mycookbook/favorites/` and the detail routes inherit
  // it. `/guest/mycookbook/` does NOT match `MY_COOKBOOK_ROUTE` above, which is
  // `/mycookbook/`, so it needs its own branch.
  if (normalized.startsWith(GUEST_MY_COOKBOOK_ROUTE)) {
    return 'cookbook';
  }
  // Order detail is only reachable from the Orders tab, and Android keeps the
  // Orders tab session alive while the user drills into it, so interactions
  // there belong to Orders too.
  if (
    normalized.startsWith(ORDERS_PLACEHOLDER_ROUTE) ||
    normalized.startsWith(ORDER_ROUTE)
  ) {
    return 'orders';
  }
  if (
    normalized.startsWith(BROWSE_ROUTE) ||
    normalized.startsWith(EDIT_DELIVERY_RECIPES_ROUTE) ||
    normalized.startsWith(EDIT_DELIVERY_GROCERIES_ROUTE)
  ) {
    return 'search';
  }
  return null;
}

/**
 * `getNavTabFromPathname` for the current `window.location`; `null` during SSR.
 */
export function getCurrentNavTab(): InteractionTab | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return getNavTabFromPathname(
    window.location.pathname,
    window.location.search
  );
}
